/**
 * The ONE canonical `media_keys.perPlatform` read-modify-write step, shared
 * by every writer in `PostBody.tsx` (aspect ratio, framing/focal+zoom,
 * placement — Task A3). Before this module the three writers each built
 * their own `{ ...existing.perPlatform, [platform]: nextEntry }` patch
 * independently:
 *
 *   - they could race each other (two near-simultaneous writes both reading
 *     the same stale render-closure `post.media_keys` snapshot, the second
 *     clobbering the first's change — the "3-writer clobber hazard" the eng
 *     review flagged as a blocker);
 *   - the aspect-ratio RESET path deleted the platform's WHOLE per-platform
 *     entry (`delete nextMap[platform]`), silently nuking any stored
 *     placement/focal/zoom alongside the ratio it meant to clear.
 *
 * This module is the pure, synchronous half of the fix: a deep-merge
 * primitive (`applyPerPlatformPatch(es)`) plus one patch-builder per writer
 * (`aspectRatioPatch` / `framingPatch` / `placementPatch`), all unit-testable
 * without React or network. The async half — refetch-before-write (guards
 * the race), `postService.updatePost`, revert-on-failure, and
 * refetch-after-write (invalidates the post query) — lives in `PostBody.tsx`
 * itself as `commitMediaKeysPatches`, beside `handleAspectRatioChange`,
 * `commitFraming`, and `commitPlacement` (it needs `postService` + React
 * Query, which don't belong in a pure module).
 */
import { type TikTokPostSettings, normalizeTikTokSettings } from '@shared/tiktok-post-params';
import type { InstagramAudioChoice } from '../../../../../../shared/instagram-publish-options';

import {
  type Placement,
  getPlacement,
  toMatrixPlatform,
} from '@/lib/platform-matrix/placement-matrix';

export interface PerPlatformEntry {
  aspectRatio?: string;
  /** Focal point of the cover-crop, 0..1 (reposition). The publish bake honors it. */
  focalX?: number;
  focalY?: number;
  /** Cover-crop zoom, >= 1. */
  zoom?: number;
  /** Cached baked-crop URL. Invalidated (deleted) by every writer here — any
   * framing/aspect/placement change means the bake no longer matches. */
  variantUrl?: string;
  /** Chosen surface within the platform (feed/reel/story/…). `undefined` =
   * "use the derived default" (Task A3's PlacementPicker). */
  placement?: Placement;
  /** TikTok settings card values (privacy, interactions, disclosure, music,
   * delivery mode). Written whole; read by dispatch at publish time. */
  tiktok?: TikTokPostSettings;
  /** Instagram: up to 3 collaborator usernames (container `collaborators`). */
  collaborators?: string[];
  /** Instagram: the place tagged on the post (container `location_id`). */
  location?: { id: string; name?: string | null };
  /** Instagram Reels: licensed audio attached with its volumes (container `audio_configuration`). */
  audio?: InstagramAudioChoice;
  /** Facebook link posts: preview overrides, honored only for links the Page owns. */
  link?: { name?: string; description?: string; picture?: string };
}

/** The author-chosen publish options, as opposed to crop geometry. */
export type PublishOptionKey = 'collaborators' | 'location' | 'audio' | 'link';

export type PerPlatformMap = Record<string, PerPlatformEntry>;

/** The full `post.media_keys` JSONB shape, loosely typed — other top-level
 * sibling keys (`format`, `disabledPlatforms`, `history`, `hasPerson`, …)
 * pass through `applyPerPlatformPatch(es)` untouched. */
export interface MediaKeysBag {
  perPlatform?: PerPlatformMap;
  [key: string]: unknown;
}

/** One platform-entry mutation: remove `unset` keys, then merge `set` keys.
 * `unset` is applied first so a patch can both clear a stale key AND set a
 * new one in the same call (e.g. placementPatch clearing `variantUrl` while
 * writing a new `placement`). */
export interface EntryPatch {
  unset?: (keyof PerPlatformEntry)[];
  set?: Partial<PerPlatformEntry>;
}

/**
 * Deep-merges ONE platform's patch into `mediaKeys`. Never whole-object
 * replaces: every other platform's entry and every other top-level sibling
 * key on `mediaKeys` pass through unchanged; the target platform's OWN
 * siblings (fields not named in `patch`) also survive untouched. If the
 * target entry ends up with zero keys after the patch, the platform key
 * itself is removed (cleanup only — `perPlatform.x === undefined` and
 * `perPlatform.x === {}` are equivalent to every reader in this codebase).
 */
export function applyPerPlatformPatch(
  mediaKeys: MediaKeysBag | null | undefined,
  platform: string,
  patch: EntryPatch,
): MediaKeysBag {
  const existing: MediaKeysBag = { ...(mediaKeys ?? {}) };
  const perPlatform: PerPlatformMap = { ...(existing.perPlatform ?? {}) };
  const nextEntry: PerPlatformEntry = { ...(perPlatform[platform] ?? {}) };

  for (const key of patch.unset ?? []) {
    delete nextEntry[key];
  }
  Object.assign(nextEntry, patch.set ?? {});

  if (Object.keys(nextEntry).length === 0) {
    delete perPlatform[platform];
  } else {
    perPlatform[platform] = nextEntry;
  }

  return { ...existing, perPlatform };
}

/**
 * Folds multiple per-platform patches into ONE `mediaKeys` result — the
 * atomic multi-platform case ("Apply framing to all platforms"): every
 * target platform is merged in a single read-modify-write cycle (one
 * `postService.updatePost` call from the caller), rather than N independent
 * round trips that could interleave with another writer.
 */
export function applyPerPlatformPatches(
  mediaKeys: MediaKeysBag | null | undefined,
  patches: { platform: string; patch: EntryPatch }[],
): MediaKeysBag {
  return patches.reduce<MediaKeysBag>(
    (acc, { platform, patch }) => applyPerPlatformPatch(acc, platform, patch),
    { ...(mediaKeys ?? {}) },
  );
}

/**
 * Aspect-ratio writer patch. `next === null` is the ORIGINAL reset path: it
 * clears every crop-only value, because native source media must not retain a
 * focal/zoom transform in the preview while dispatch sends the untouched URL.
 * Placement survives: it is a destination choice rather than crop geometry.
 * Setting a ratio always drops `variantUrl` too (a new ratio invalidates
 * whatever was baked at the old one).
 */
export function aspectRatioPatch(next: string | null): EntryPatch {
  if (next == null) return { unset: ['aspectRatio', 'variantUrl', 'focalX', 'focalY', 'zoom'] };
  return { unset: ['variantUrl'], set: { aspectRatio: next } };
}

/**
 * Framing (focal/zoom) writer patch. Always drops the stale baked
 * `variantUrl` (publish must re-crop at the new focal/zoom) and, when
 * `effectiveAspectRatio` is supplied (the caller's existing override or the
 * platform's recommended default — see `recommendedAspectRatio`), pins it
 * onto the entry so the bake has a ratio to crop against. Mirrors the
 * pre-A3 `commitFraming` behavior exactly.
 */
export function framingPatch(
  patch: { focalX?: number; focalY?: number; zoom?: number },
  effectiveAspectRatio: string | undefined,
): EntryPatch {
  return {
    unset: ['variantUrl'],
    set: {
      ...patch,
      ...(effectiveAspectRatio ? { aspectRatio: effectiveAspectRatio } : {}),
    },
  };
}

/**
 * Placement writer patch (Task A3's `commitPlacement`). ANY placement
 * change — to a specific surface OR back to `undefined` (reset to derived)
 * — clears that platform's `aspectRatio` override + stale `variantUrl`:
 * both belonged to whichever surface was effective BEFORE this change (a
 * feed 4:5 crop doesn't carry over to a 9:16 Story) and aren't guaranteed
 * valid for the new one. `focalX`/`focalY`/`zoom` are left untouched — a
 * chosen focal point is still a reasonable starting point on the new
 * surface's crop.
 */
export function placementPatch(next: Placement | undefined): EntryPatch {
  if (next === undefined) {
    return { unset: ['aspectRatio', 'variantUrl', 'placement'] };
  }
  return { unset: ['aspectRatio', 'variantUrl'], set: { placement: next } };
}

/**
 * TikTok settings writer patch. Writes the whole settings object (the merge RPC
 * replaces a platform's key wholesale) and touches nothing about the crop, so
 * choosing a privacy level never invalidates a baked variant.
 */
export function tiktokSettingsPatch(settings: TikTokPostSettings): EntryPatch {
  return { set: { tiktok: settings } };
}

/**
 * Reads the TikTok settings back out of a `perPlatform` map. They live one level
 * down, at `perPlatform.tiktok.tiktok`: the first `tiktok` is the platform's
 * entry (like `instagram`), the second is the settings key inside that entry.
 * The dispatcher reads the same path (`perPlatformCfg.tiktok`).
 */
export function readTikTokSettings(
  perPlatform: PerPlatformMap | null | undefined,
): TikTokPostSettings | null {
  return normalizeTikTokSettings(perPlatform?.tiktok?.tiktok);
}

/**
 * Publish-option writer patch (collaborators, location, audio, link preview).
 * A key set to `null`/empty is removed. These never touch crop geometry, so a
 * crop, focal point or placement survives an options change.
 */
export function publishOptionsPatch(
  next: Partial<Record<PublishOptionKey, PerPlatformEntry[PublishOptionKey] | null>>,
): EntryPatch {
  const set: Partial<PerPlatformEntry> = {};
  const unset: (keyof PerPlatformEntry)[] = [];
  for (const [key, value] of Object.entries(next) as [PublishOptionKey, unknown][]) {
    const empty =
      value == null ||
      (Array.isArray(value) && value.length === 0) ||
      (typeof value === 'object' &&
        !Array.isArray(value) &&
        Object.keys(value as object).length === 0);
    if (empty) unset.push(key);
    else (set as Record<string, unknown>)[key] = value;
  }
  return { ...(unset.length ? { unset } : {}), ...(Object.keys(set).length ? { set } : {}) };
}

/** The platform's primary-surface recommended aspect ratio — the framing
 * writer's historical backfill when no explicit override exists yet. It is
 * advisory only: user interaction must never call it to create a crop. */
export function recommendedAspectRatio(platform: string): string | undefined {
  const mp = toMatrixPlatform(platform);
  return mp ? getPlacement(mp)?.recommendedRatio : undefined;
}

/**
 * Builds a framing patch from an existing destination command. Native media
 * deliberately stays native: a drag/zoom does not convert an absent command
 * into the platform's recommended crop. Callers should offer framing only once
 * the creator has selected an explicit aspect ratio.
 */
export function framingPatchForPlatform(
  patch: { focalX?: number; focalY?: number; zoom?: number },
  entry: Pick<PerPlatformEntry, 'aspectRatio'> | null | undefined,
): EntryPatch {
  return framingPatch(patch, entry?.aspectRatio);
}

/**
 * Task A4: flattens an `EntryPatch` into the JSON-object shape the atomic
 * `merge_post_media_keys` RPC expects — `unset` keys become explicit JSON
 * `null` (the RPC's delete-key signal, mirrors this same unset-then-set
 * order so a patch can both clear a stale key AND set a new one), `set`
 * keys pass through as-is. Pure and RPC-payload-agnostic on purpose: the
 * caller (mediaKeysCommitQueue.ts) owns the actual `postService` call.
 */
export function entryPatchToJsonPatch(patch: EntryPatch): Record<string, unknown> {
  const result: Record<string, unknown> = {};
  for (const key of patch.unset ?? []) {
    result[key] = null;
  }
  Object.assign(result, patch.set ?? {});
  return result;
}
